// ── OpportunitySourcesPage — recreation of the PowerBI "Opportunity Sources" dashboard ──
// All opportunities regardless of status. Excludes opps owned by the four named owners.
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt } from "../../../shared/format";
import { exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { OwnerMultiSelect, isExcludedOwner } from "./analyticsShared";

export const OpportunitySourcesPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // slicers
  const [company, setCompany] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Sourced By multi-select; null = default (all except excluded four)
  const [typeSel, setTypeSel] = useState("");
  const [sourceSel, setSourceSel] = useState(new Set());  // multi-select lead sources (empty = all)
  const [sourceOpen, setSourceOpen] = useState(false);
  const [sourceFirm, setSourceFirm] = useState("");   // click-to-filter from firm cards
  // date-range slicers: [from, to] per field
  const [created, setCreated] = useState({ from: "", to: "" });
  const [tsIssuedRange, setTsIssuedRange] = useState({ from: "", to: "" });
  const [tsSignedRange, setTsSignedRange] = useState({ from: "", to: "" });
  const [closingRange, setClosingRange] = useState({ from: "", to: "" });

  const pickSource = (s) => setSourceSel(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const pickFirm = (s) => setSourceFirm(prev => prev === s ? "" : s);

  const inRange = (val, range) => {
    if (!range.from && !range.to) return true;
    if (!val) return false;
    const d = new Date(val); if (isNaN(d)) return false;
    if (range.from && d < new Date(range.from)) return false;
    if (range.to && d > new Date(range.to)) return false;
    return true;
  };

  const load = () => {
    setLoading(true);
    api.get("getOpportunities")
      .then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const commit = (x) => parseFloat(x.commitment_amount || 0);

  // Base: all opps. The four owners are excluded by default via the Sourced By filter (below),
  // but remain re-selectable rather than hard-removed.
  const base = useMemo(() => opps, [opps]);

  const owners = useMemo(() => [...new Set(base.map(o => o.ownerName).filter(Boolean))].sort(), [base]);
  const types = useMemo(() => [...new Set(base.map(o => o.collateraltype).filter(Boolean))].sort(), [base]);
  const sources = useMemo(() => [...new Set(base.map(o => o.lead_source || "Unspecified"))].sort(), [base]);

  // Default Sourced By to every owner except the excluded four; unassigned (no owner) always shown.
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  // Apply slicers.
  const rows = useMemo(() => {
    const c = company.trim().toLowerCase();
    return base.filter(o => {
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (!ownerOk(o.ownerName)) return false;
      if (typeSel && o.collateraltype !== typeSel) return false;
      if (sourceSel.size && !sourceSel.has(o.lead_source || "Unspecified")) return false;
      if (sourceFirm && (o.source_firm || "Unspecified") !== sourceFirm) return false;
      if (!inRange(o.createdat, created)) return false;
      if (!inRange(o.term_sheet_issued_date, tsIssuedRange)) return false;
      if (!inRange(o.term_sheet_signed_date, tsSignedRange)) return false;
      if (!inRange(o.account_closing_date, closingRange)) return false;
      return true;
    });
  }, [base, company, ownerSel, typeSel, sourceSel, sourceFirm, created, tsIssuedRange, tsSignedRange, closingRange]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  // ── breakdown by lead source for a given subset ──
  const breakdown = (subset) => {
    const totCnt = subset.length;
    const totAmt = subset.reduce((s, o) => s + commit(o), 0);
    const m = new Map();
    subset.forEach(o => {
      const k = o.lead_source || "Unspecified";
      const e = m.get(k) || { source: k, cnt: 0, amt: 0 };
      e.cnt += 1; e.amt += commit(o);
      m.set(k, e);
    });
    const list = [...m.values()].sort((a, b) => b.amt - a.amt);
    return { list, totCnt, totAmt };
  };

  const totalOpps = rows;
  const tsIssued = rows.filter(o => o.term_sheet_issued_date);
  const tsSigned = rows.filter(o => o.term_sheet_signed_date);
  const closings = rows.filter(o => o.is_won);

  // ── source firm rankings ──
  const firmMap = new Map();
  rows.forEach(o => {
    const k = o.source_firm || "Unspecified";
    const e = firmMap.get(k) || { firm: k, cnt: 0, amt: 0 };
    e.cnt += 1; e.amt += commit(o);
    firmMap.set(k, e);
  });
  const firms = [...firmMap.values()];
  // By Count: exclude firm names of 2 or fewer characters (e.g. stray initials/codes).
  const topFirmsByCount = firms.filter(f => (f.firm || "").trim().length > 2).sort((a, b) => b.cnt - a.cnt).slice(0, 5);
  const topFirmsByAmt = [...firms].sort((a, b) => b.amt - a.amt).slice(0, 5);

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0) + "%";

  const BreakdownCard = ({ title, data }) => (
    <Card style={{ padding: 16, overflowX: "auto" }}>
      <div style={cardTitle}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <Th>Lead Source</Th><Th style={{ textAlign: "right" }}>Cnt</Th><Th style={{ textAlign: "right" }}>%</Th>
          <Th style={{ textAlign: "right" }}>Commitment</Th><Th style={{ textAlign: "right" }}>%</Th>
        </tr></thead>
        <tbody>{data.list.length === 0 ? <tr><Td colSpan={5} style={{ color: "var(--color-text-secondary)" }}>No opportunities.</Td></tr> : data.list.map(r => {
          const active = sourceSel.has(r.source);
          return (
          <tr key={r.source} title="Click to filter the page by this lead source" style={{ cursor: "pointer", background: active ? B.cyanLight : "" }}
            onClick={() => pickSource(r.source)}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--color-background-secondary)"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = ""; }}>
            <Td style={{ fontWeight: active ? 700 : 400 }}>{r.source}</Td>
            <TdNum>{r.cnt}</TdNum>
            <TdNum>{pct(r.cnt, data.totCnt)}</TdNum>
            <TdNum>{fmt(r.amt)}</TdNum>
            <TdNum>{pct(r.amt, data.totAmt)}</TdNum>
          </tr>
          );
        })}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td style={{ padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total</td>
          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{data.totCnt}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(data.totAmt)}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>
  );

  const FirmCard = ({ title, data, valueFn, valueRender }) => (
    <Card style={{ padding: 16, overflowX: "auto" }}>
      <div style={cardTitle}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr><Th>Source Firm</Th><Th style={{ textAlign: "right" }}>{valueRender ? "Commitment" : "Count"}</Th></tr></thead>
        <tbody>{data.length === 0 ? <tr><Td colSpan={2} style={{ color: "var(--color-text-secondary)" }}>None.</Td></tr> : data.map(r => {
          const active = sourceFirm === r.firm;
          return (
          <tr key={r.firm} title="Click to filter the page by this source firm" style={{ cursor: "pointer", background: active ? B.cyanLight : "" }}
            onClick={() => pickFirm(r.firm)}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--color-background-secondary)"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = ""; }}>
            <Td style={{ fontWeight: active ? 700 : 400 }}>{r.firm}</Td><TdNum>{valueRender ? fmt(valueFn(r)) : valueFn(r)}</TdNum>
          </tr>
          );
        })}</tbody>
      </table>
    </Card>
  );

  const detailCols = ["collateraltype", "companyname", "ownerName", "lead_source", "source_firm", "commitment_amount", "stage", "termination_reason"];
  const detailSorted = [...rows].sort((a, b) => commit(b) - commit(a));

  return <>
    <SectionHeader title="Opportunity Sources" actions={[
      <ExportBtn key="ex" onClick={() => exportCSV(detailSorted, detailCols, "opportunity_sources.csv")}>Export CSV</ExportBtn>
    ]} />

    {/* slicers */}
    <Card style={{ padding: 12, marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…"
        style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)", minWidth: 180 }} />

      <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />

      <div style={{ position: "relative" }}>
        <Btn onClick={() => setSourceOpen(v => !v)}>
          Lead Source{sourceSel.size ? " (" + sourceSel.size + ")" : ": All"} ▾
        </Btn>
        {sourceOpen && (
          <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, marginTop: 4, background: "#fff", border: "1px solid var(--color-border-secondary)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 8, maxHeight: 300, overflowY: "auto", minWidth: 200 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
              <button onClick={() => setSourceSel(new Set())} style={{ fontSize: 11, background: "none", border: "none", color: B.cyan, cursor: "pointer" }}>Clear</button>
              <button onClick={() => setSourceOpen(false)} style={{ fontSize: 11, background: "none", border: "none", color: "var(--color-text-secondary)", cursor: "pointer" }}>Close</button>
            </div>
            {sources.map(s => (
              <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 2px", cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={sourceSel.has(s)} onChange={e => {
                  const next = new Set(sourceSel);
                  e.target.checked ? next.add(s) : next.delete(s);
                  setSourceSel(next);
                }} /> {s}
              </label>
            ))}
          </div>
        )}
      </div>

      <select value={typeSel} onChange={e => setTypeSel(e.target.value)}
        style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
        <option value="">All Loan Types</option>
        {types.map(t => <option key={t} value={t}>{t}</option>)}
      </select>

      {[...sourceSel].map(s => (
        <button key={s} onClick={() => pickSource(s)} title="Remove this lead source"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>
          {s} <span style={{ fontWeight: 700 }}>✕</span>
        </button>
      ))}
      {sourceFirm && (
        <button onClick={() => setSourceFirm("")} title="Clear source firm filter"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>
          Source Firm: {sourceFirm} <span style={{ fontWeight: 700 }}>✕</span>
        </button>
      )}

      <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--color-text-secondary)" }}>
        {rows.length} opportunities · {fmt(rows.reduce((s, o) => s + commit(o), 0))}
      </div>
    </Card>

    {/* date-range slicers — one card each, spanning the top */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      {[
        { label: "Opportunity Created", range: created, set: setCreated },
        { label: "TS Issued", range: tsIssuedRange, set: setTsIssuedRange },
        { label: "TS Signed", range: tsSignedRange, set: setTsSignedRange },
        { label: "Closing (first funded)", range: closingRange, set: setClosingRange },
      ].map(({ label, range, set }) => {
        const dstyle = { padding: "5px 8px", fontSize: 12, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)", width: "100%" };
        const active = !!(range.from || range.to);
        return (
          <Card key={label} style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
              {active && (
                <button onClick={() => set({ from: "", to: "" })} title="Clear"
                  style={{ background: "none", border: "none", color: B.cyan, cursor: "pointer", fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✕</button>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
                <span style={{ width: 30 }}>From</span>
                <input type="date" value={range.from} onChange={e => set({ ...range, from: e.target.value })} style={dstyle} />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
                <span style={{ width: 30 }}>To</span>
                <input type="date" value={range.to} onChange={e => set({ ...range, to: e.target.value })} style={dstyle} />
              </label>
            </div>
          </Card>
        );
      })}
    </div>

    {/* four lead-source breakdowns */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
      <BreakdownCard title="Total Opportunities" data={breakdown(totalOpps)} />
      <BreakdownCard title="Term Sheets Issued" data={breakdown(tsIssued)} />
      <BreakdownCard title="Term Sheets Signed" data={breakdown(tsSigned)} />
      <BreakdownCard title="Closings (Funded)" data={breakdown(closings)} />
    </div>

    {/* top 5 source firms */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
      <FirmCard title="Top 5 Source Firms by Count" data={topFirmsByCount} valueFn={r => r.cnt} />
      <FirmCard title="Top 5 Source Firms by Commitment" data={topFirmsByAmt} valueFn={r => r.amt} valueRender />
    </div>

    {/* detail table */}
    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, padding: "12px 16px 6px", marginBottom: 0 }}>Opportunity Detail</div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <Th>Type</Th><Th>Name</Th><Th>Sourced By</Th><Th>Lead Source</Th><Th>Source Firm</Th>
          <Th style={{ textAlign: "right" }}>Commitment</Th><Th>Stage</Th><Th>Termination Reason</Th>
        </tr></thead>
        <tbody>{detailSorted.length === 0 ? <tr><Td colSpan={8} style={{ color: "var(--color-text-secondary)" }}>No opportunities.</Td></tr> : detailSorted.map(o => (
          <tr key={o.id} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td>{o.collateraltype || "—"}</Td>
            <Td style={{ fontWeight: 500 }}>{o.companyname || o.name || "—"}</Td>
            <Td>{o.ownerName || "—"}</Td>
            <Td>{o.lead_source || "—"}</Td>
            <Td>{o.source_firm || "—"}</Td>
            <TdNum>{fmt(commit(o))}</TdNum>
            <Td>{o.stage || "—"}</Td>
            <Td>{o.termination_reason || "—"}</Td>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={5} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailSorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailSorted.reduce((s, o) => s + commit(o), 0))}</td>
          <td colSpan={2} style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
